import { useState, useMemo, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Eye, EyeOff, KeyRound, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch, setAccessToken } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ChangePasswordResponse } from '@uniticket/shared';

// ── Password policy rules ──────────────────────────────────────

interface PolicyRule {
  key: string;
  i18nKey: string;
  test: (pw: string) => boolean;
}

const POLICY_RULES: PolicyRule[] = [
  { key: 'length', i18nKey: 'changePassword.rules.minLength', test: (pw) => pw.length >= 12 },
  { key: 'uppercase', i18nKey: 'changePassword.rules.uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lowercase', i18nKey: 'changePassword.rules.lowercase', test: (pw) => /[a-z]/.test(pw) },
  { key: 'number', i18nKey: 'changePassword.rules.number', test: (pw) => /[0-9]/.test(pw) },
];

// ── Password strength calculator ───────────────────────────────

function getPasswordStrength(pw: string): { score: number; i18nKey: string; color: string } {
  if (!pw) return { score: 0, i18nKey: '', color: 'bg-slate-200' };

  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  if (score <= 1) return { score: 1, i18nKey: 'changePassword.strength.weak', color: 'bg-red-500' };
  if (score <= 2) return { score: 2, i18nKey: 'changePassword.strength.fair', color: 'bg-orange-500' };
  if (score <= 3) return { score: 3, i18nKey: 'changePassword.strength.good', color: 'bg-amber-500' };
  if (score <= 4) return { score: 4, i18nKey: 'changePassword.strength.strong', color: 'bg-green-500' };
  return { score: 5, i18nKey: 'changePassword.strength.veryStrong', color: 'bg-emerald-500' };
}

// ── ChangePasswordPage ─────────────────────────────────────────

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['profile', 'common']);
  const { user, updateUser } = useAuth();

  // Redirect Entra ID users -- they manage passwords via Microsoft
  const isEntra = user?.authType === 'ENTRA_ID';

  useEffect(() => {
    if (isEntra) {
      navigate('/profile', { replace: true });
    }
  }, [isEntra, navigate]);

  // Form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Visibility toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  // Policy check results
  const policyResults = useMemo(
    () => POLICY_RULES.map((rule) => ({ ...rule, met: rule.test(newPassword) })),
    [newPassword],
  );

  const allPolicyMet = policyResults.every((r) => r.met);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  // Form validity
  const canSubmit =
    currentPassword.length > 0 && allPolicyMet && passwordsMatch && !submitting;

  // ── Submit handler ──────────────────────────────────────────

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const response = await apiFetch<ChangePasswordResponse>('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      // Update access token and user in auth context
      setAccessToken(response.accessToken);
      updateUser(response.user);

      toast.success(t('changePassword.success'));
      navigate('/profile');
    } catch (err: any) {
      toast.error(err.message || t('changePassword.error'));
    } finally {
      setSubmitting(false);
    }
  }

  // Don't render while redirecting Entra users
  if (isEntra) return null;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold gradient-text">{t('changePassword.title')}</h1>
          <p className="text-sm text-slate-500">{t('changePassword.subtitle')}</p>
        </div>
      </div>

      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate('/profile')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-rose transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('changePassword.backToProfile')}
      </button>

      {/* Password form card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-brand-rose" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">
            {t('changePassword.updatePassword')}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Current password */}
          <div className="space-y-2">
            <Label htmlFor="current-password">{t('changePassword.currentPassword')}</Label>
            <div className="relative">
              <Input
                id="current-password"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t('changePassword.currentPlaceholder')}
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                tabIndex={-1}
                aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div className="space-y-2">
            <Label htmlFor="new-password">{t('changePassword.newPassword')}</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('changePassword.newPlaceholder')}
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                tabIndex={-1}
                aria-label={showNew ? 'Hide new password' : 'Show new password'}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Strength indicator */}
            {newPassword.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        level <= strength.score ? strength.color : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>
                <p className={`text-xs font-medium ${
                  strength.score <= 1 ? 'text-red-500' :
                  strength.score <= 2 ? 'text-orange-500' :
                  strength.score <= 3 ? 'text-amber-500' :
                  'text-green-600'
                }`}>
                  {strength.i18nKey ? t(strength.i18nKey) : ''}
                </p>
              </div>
            )}

            {/* Policy checklist */}
            <div className="mt-3 space-y-1.5">
              {policyResults.map((rule) => (
                <div key={rule.key} className="flex items-center gap-2">
                  <div
                    className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                      rule.met ? 'bg-green-500' : 'bg-slate-200'
                    }`}
                  >
                    <Check
                      className={`w-2.5 h-2.5 transition-colors ${
                        rule.met ? 'text-white' : 'text-slate-400'
                      }`}
                    />
                  </div>
                  <span
                    className={`text-xs transition-colors ${
                      rule.met ? 'text-green-700 font-medium' : 'text-slate-500'
                    }`}
                  >
                    {t(rule.i18nKey)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Confirm password */}
          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('changePassword.confirmPassword')}</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('changePassword.confirmPlaceholder')}
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                tabIndex={-1}
                aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-red-500">{t('common:validation.passwordsDoNotMatch')}</p>
            )}
            {passwordsMatch && (
              <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                <Check className="w-3 h-3" />
                {t('common:validation.passwordsMatch')}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/profile')}
              disabled={submitting}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-brand-gradient text-white hover:opacity-90"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('changePassword.changingPassword')}
                </>
              ) : (
                t('changePassword.title')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
